import { useStore } from "zustand";
import { createStore, type StoreApi } from "zustand/vanilla";

import { onExit } from "../api/exitFlush";
import { t, tc } from "../i18n/i18n";
import { type Notice, useUi, type ViewKey } from "../state/ui";
import { zoneRefusalOf } from "./zones/zoneRefusal";

/** Annuler / Rétablir de la carte (K14, docs/14 §2.8) : moteur générique, indépendant de l'API. Chaque canal
 *  (dispositif, graphiques, ponts, potentiel, annotations) fournit de quoi relever son état (`snapshot`) et l'envoyer
 *  (`apply`) ; l'historique garde des paires d'états (avant, après) avec un libellé, unique pour le dossier,
 *  plafonné à 50 entrées et non persisté.
 *
 *  Plusieurs ⌘Z rapides : l'aperçu des canaux rendus côté client (fuseau, LIMA, graphiques) suit à chaque
 *  touche (`preview`), et seul le dernier état de chaque canal est envoyé, 300 ms après la dernière touche, une
 *  requête à la fois (les unités suivent à la réponse, par la transition de 900 ms du canal). Remise à zéro sur
 *  412 / 409 (le dossier est rechargé), au changement de base du dispositif, de dossier ou d'OPO en CENTAC.
 *  Actif seulement quand la carte est la surface visible. */

export type HistoryChannelKey = "dispositif" | "graphiques" | "ponts" | "potentiel" | "annotations";

export const HISTORY_CHANNELS: readonly HistoryChannelKey[] = ["dispositif", "graphiques", "ponts", "potentiel", "annotations"];

/** Entrées gardées au plus (les plus anciennes tombent). */
export const HISTORY_CAP = 50;

/** Délai d'envoi après la dernière touche (ms). */
export const HISTORY_FLUSH_MS = 300;

/** Clés du canal dispositif toujours présentes dans un instantané, `null` si absentes : côté serveur, un champ
 *  absent veut dire « conservé », et le premier ⌘Z après une première retouche du fuseau (ou le premier
 *  glissement d'une ligne de la ZO, `measures`) ne déferait rien. */
export const DISPOSITIF_NULLABLE_KEYS: readonly string[] = ["corridor", "missions", "anchored", "measures", "orientation"];

/** Vues plein cadre au-dessus de la carte (`z-20` dans App.tsx) : l'historique de la carte y est inactif. */
export const FULL_FRAME_VIEWS: readonly ViewKey[] = [
  "country",
  "briefing",
  "orbats",
  "received",
  "declination",
  "retex",
  "evaluation",
  "timeline",
  "coa",
  "order",
];

/** Instantané normalisé : chaque clé de `keys` explicitement présente, `null` si elle manquait. */
export function normalizeSnapshot<T extends object>(state: T, keys: readonly string[] = DISPOSITIF_NULLABLE_KEYS): T {
  const out = { ...state } as Record<string, unknown>;
  for (const key of keys) out[key] = out[key] ?? null;
  return out as T;
}

/** La carte est la surface visible : ni accueil, ni vue plein cadre. (La saisie focalisée se teste à l'appui.) */
export function mapSurfaceVisible(ui: { welcome: boolean; view: ViewKey }): boolean {
  return !ui.welcome && !FULL_FRAME_VIEWS.includes(ui.view);
}

/** Égalité profonde de deux états JSON (l'ordre des clés ne compte pas). */
export function sameState(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a === null || b === null || typeof a !== "object" || typeof b !== "object") return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  if (Array.isArray(a)) {
    const other = b as unknown[];
    return a.length === other.length && a.every((v, i) => sameState(v, other[i]));
  }
  const ka = Object.keys(a).filter((k) => (a as Record<string, unknown>)[k] !== undefined);
  const kb = Object.keys(b).filter((k) => (b as Record<string, unknown>)[k] !== undefined);
  return (
    ka.length === kb.length &&
    ka.every((k) => sameState((a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k]))
  );
}

/** Refus qui veut dire « le dossier a changé ailleurs » : verrou périmé (412) ou conflit (409). Un 409 propre à la
 *  zone libre (terrain en préparation, préparation en échec : `zoneRefusalOf`) n'en est pas un : rien n'a changé
 *  ailleurs, l'historique est gardé et l'action attendue est désignée (`handleZoneRefusal`). */
export function isConflict(error: unknown): boolean {
  const status = (error as { status?: unknown } | null)?.status;
  if (status === 409 && zoneRefusalOf(error)) return false;
  if (sessionRefusal(error)) return false;
  return status === 412 || status === 409;
}

/** Refus qui tient à la session de la personne (docs/17 §4.8, §8.2) : verrouillée, fermée ou expirée (401), ouverte
 *  pour un autre compte (409 `ACCOUNT_MISMATCH`). Rien n'a changé ailleurs : ni conflit ni avis, le portail des
 *  comptes pose sa surcouche ou recharge la page. Codes lus tels quels (pas d'import du client ici). */
export function sessionRefusal(error: unknown): boolean {
  const { status, code } = (error as { status?: unknown; code?: unknown } | null) ?? {};
  if (status === 409) return code === "ACCOUNT_MISMATCH";
  return status === 401 && (code === "AUTH_REQUIRED" || code === "SESSION_LOCKED");
}

/** Canal de l'historique, branché par le hook qui écrit cet état (useUnitMoves, useGraphics, useBridgeState,
 *  potentiel). Contrat d'écriture : avant toute écriture du canal (son `mutateAsync`), le hook attend
 *  `history.settle()`, pour que son action parte après les annulations en attente et ne soit pas écrasée par
 *  elles ; il relève l'état d'avant par `capture` et enregistre l'action par `commit` à la réponse. */
export interface HistoryChannel<S = unknown> {
  /** État courant tel que le serveur l'a renvoyé ; `null` : indisponible (dossier non enregistré). */
  snapshot: () => S | null;
  /** Envoie l'état (PUT du canal, portée de mutation du dossier) ; se résout à la réponse, une fois le cache mis
   *  à jour, et rejette avec l'erreur de l'API (son `status` distingue 412 / 409). */
  apply: (state: S) => Promise<unknown>;
  /** Aperçu immédiat côté client avant l'envoi ; `null` : retirer l'aperçu (la réponse fait foi). */
  preview?: (state: S | null) => void;
  /** Normalisation propre au canal. Pour le dispositif, elle s'ajoute à `normalizeSnapshot` (appliquée après),
   *  sans la remplacer : corridor, missions, anchored et measures restent toujours explicites (`eni_roles` à l'étape 3). */
  normalize?: (state: S) => S;
  /** Égalité de deux états (par défaut, `sameState`). */
  equals?: (a: S, b: S) => boolean;
}

/** Changement d'un canal : états d'avant et d'après. */
export interface HistoryPart {
  channel: HistoryChannelKey;
  before: unknown;
  after: unknown;
}

export interface HistoryEntry extends HistoryPart {
  /** Action défaite, déjà traduite et en minuscule (« déplacement de 2e SGTIA »). */
  label: string;
  /** Entrée composée (sens de la manœuvre, docs/14 §2.14) : autres canaux changés par la même action (graphiques
   *  tournés avec le dispositif) ; un ⌘Z les restaure tous, le dispositif d'abord. */
  parts?: HistoryPart[];
}

/** Ce qui remet l'historique à zéro quand il change. `opo` : seulement en CENTAC (`null` ailleurs) ; `basis` :
 *  même tuple que `_placement_basis` du serveur, plus `map_id`, sérialisé. */
export interface HistoryContext {
  dossier: string | null;
  basis: string | null;
  opo: string | null;
}

export type HistoryNotice = Omit<Notice, "id">;

export interface HistoryOptions {
  notify: (notice: HistoryNotice) => void;
  /** 412 / 409 : recharger le dossier (l'historique est déjà remis à zéro et l'avis affiché). */
  onConflict: (error: unknown) => void;
  /** Refus propre à une zone libre (terrain pas prêt, fuseau hors de la zone…) : avis avec l'action attendue,
   *  titré `title` ; vrai s'il a été traité (sinon l'avis générique). Branché par `dossierWrites` pour garder le
   *  moteur indépendant du cache. */
  onZoneRefusal: (error: unknown, title: string) => boolean;
  /** Réponse d'un envoi qui fait suite à ⌘Z ou ⇧⌘Z : avis « Annulé : … » complété (mention des ordres recomposés et
   *  action vers l'ordre), ou null pour le laisser tel quel. Branché par `dossierWrites`, comme `onZoneRefusal`. */
  complete: (notice: HistoryNotice, result: unknown) => HistoryNotice | null;
  setTimer: (run: () => void, ms: number) => unknown;
  clearTimer: (timer: unknown) => void;
  cap: number;
  flushMs: number;
}

/** État observable, pour les boutons ↶ ↷. */
export interface HistoryState {
  canUndo: boolean;
  canRedo: boolean;
  /** Un envoi attend ou est en cours. */
  busy: boolean;
  /** La carte est la surface visible. */
  active: boolean;
  undoLabel: string | null;
  redoLabel: string | null;
}

export interface History {
  readonly store: StoreApi<HistoryState>;
  /** Branche un canal ; rend son débranchement. */
  registerChannel<S>(key: HistoryChannelKey, channel: HistoryChannel<S>): () => void;
  /** Instantané normalisé de l'état courant d'un canal (`null` : canal absent ou indisponible). */
  capture<S>(key: HistoryChannelKey): S | null;
  /** Ajoute une action enregistrée par le serveur ; efface ce qui pouvait être rétabli. */
  record<S>(key: HistoryChannelKey, before: S, after: S, label: string): void;
  /** Comme `record`, l'état d'après relevé sur le canal (à appeler une fois le cache à jour). */
  commit<S>(key: HistoryChannelKey, before: S | null, label: string): void;
  /** Entrée composée : une action qui a changé plusieurs canaux (`main` puis `parts`), défaite d'un seul ⌘Z. */
  recordMany(main: HistoryPart, parts: readonly HistoryPart[], label: string): void;
  undo(): boolean;
  redo(): boolean;
  reset(): void;
  setContext(context: HistoryContext): void;
  setActive(active: boolean): void;
  isActive(): boolean;
  configure(options: Partial<HistoryOptions>): void;
  /** Envoie tout de suite ce qui attend (sortie de la carte, tests) ; se résout à la fin de l'envoi lancé. */
  flush(): Promise<void>;
  /** Envoie tout de suite ce qui attend et se résout quand plus rien n'attend ni n'est en cours : à attendre
   *  par un hook avant d'écrire un canal (voir `HistoryChannel`). */
  settle(): Promise<void>;
}

interface Pending {
  target: unknown;
  /** État que le serveur a quand le canal entre dans le groupe (rien n'est envoyé si on y revient). */
  baseline: unknown;
}

export function createHistory(options: Partial<HistoryOptions> = {}): History {
  const opts: HistoryOptions = {
    notify: () => {},
    onConflict: () => {},
    onZoneRefusal: () => false,
    complete: () => null,
    setTimer: (run, ms) => setTimeout(run, ms),
    clearTimer: (timer) => clearTimeout(timer as ReturnType<typeof setTimeout>),
    cap: HISTORY_CAP,
    flushMs: HISTORY_FLUSH_MS,
    ...options,
  };
  const channels = new Map<HistoryChannelKey, HistoryChannel<unknown>>();
  let entries: HistoryEntry[] = [];
  let cursor = 0; // entrées appliquées
  const pending = new Map<HistoryChannelKey, Pending>();
  let groupStart: number | null = null; // curseur au début du groupe de touches
  let appliedInGroup = false;
  let timer: unknown = null;
  let inFlight: Promise<void> | null = null;
  let again = false;
  let generation = 0;
  let active = true;
  let context: HistoryContext | null = null;
  // Avis du dernier ⌘Z / ⇧⌘Z, complété à la réponse du serveur (`complete`) ; oublié à la prochaine action.
  let stepNotice: HistoryNotice | null = null;

  const store = createStore<HistoryState>(() => ({
    canUndo: false,
    canRedo: false,
    busy: false,
    active,
    undoLabel: null,
    redoLabel: null,
  }));

  const partsOf = (entry: HistoryEntry): HistoryPart[] => [entry, ...(entry.parts ?? [])];
  // Entrée composée : utilisable seulement si tous ses canaux sont branchés.
  const usable = (entry: HistoryEntry | undefined) => Boolean(entry && partsOf(entry).every((part) => channels.has(part.channel)));
  const emit = () =>
    store.setState({
      canUndo: active && usable(entries[cursor - 1]),
      canRedo: active && usable(entries[cursor]),
      busy: timer !== null || inFlight !== null || pending.size > 0,
      active,
      undoLabel: entries[cursor - 1]?.label ?? null,
      redoLabel: entries[cursor]?.label ?? null,
    });

  const normalizer = (key: HistoryChannelKey) => {
    const custom = channels.get(key)?.normalize ?? ((s: unknown) => s);
    if (key !== "dispositif") return custom;
    return (s: unknown) => custom(s && typeof s === "object" ? normalizeSnapshot(s) : s);
  };
  const equality = (key: HistoryChannelKey) => channels.get(key)?.equals ?? sameState;

  const clearTimer = () => {
    if (timer !== null) opts.clearTimer(timer);
    timer = null;
  };

  const dropPending = () => {
    for (const key of pending.keys()) channels.get(key)?.preview?.(null);
    pending.clear();
    groupStart = null;
    appliedInGroup = false;
  };

  const reset = () => {
    stepNotice = null;
    generation++;
    clearTimer();
    dropPending();
    entries = [];
    cursor = 0;
    emit();
  };

  const failure = (error: unknown, key: HistoryChannelKey) => {
    channels.get(key)?.preview?.(null); // le canal refusé n'attend plus : son aperçu est retiré ici
    if (isConflict(error)) {
      reset();
      opts.notify({ text: t("Le dossier a été modifié ailleurs : historique remis à zéro"), tone: "warn" });
      opts.onConflict(error);
      return;
    }
    // Autre refus : l'aperçu revient à l'état du serveur. Rien d'envoyé dans ce groupe : le curseur revient à son
    // début ; sinon l'historique ne correspond plus au serveur et repart de zéro.
    const restore = appliedInGroup ? null : groupStart;
    dropPending();
    if (restore === null) {
      entries = [];
      cursor = 0;
    } else cursor = restore;
    if (zoneRefusalOf(error) !== null && opts.onZoneRefusal(error, t("Annulation non enregistrée"))) {
      emit();
      return;
    }
    // Session de la personne refusée : le portail des comptes s'en charge, sans avis de plus.
    if (sessionRefusal(error)) {
      emit();
      return;
    }
    opts.notify({
      text: t("Annulation non enregistrée"),
      // Message du serveur seulement pour une erreur de l'API (elle porte un `status`) ; une panne réseau
      // (TypeError « Failed to fetch ») n'a qu'un message brut du navigateur, en anglais.
      detail:
        error instanceof Error && typeof (error as { status?: unknown }).status === "number" && error.message
          ? error.message
          : t("Serveur injoignable"),
      tone: "warn",
    });
    emit();
  };

  /** Envoie, canal par canal et une requête à la fois, le dernier état demandé. */
  const send = async (gen: number) => {
    while (pending.size && gen === generation && timer === null) {
      // Le dispositif part toujours en premier (entrée composée : les graphiques tournés sont contrôlés dans
      // l'emprise du dispositif déjà enregistré).
      const first = pending.has("dispositif") ? "dispositif" : (pending.keys().next().value as HistoryChannelKey);
      const key = first;
      const item = pending.get(key)!;
      pending.delete(key);
      const channel = channels.get(key);
      if (!channel) continue;
      if (equality(key)(item.target, item.baseline)) {
        if (!pending.has(key)) channel.preview?.(null);
        continue;
      }
      let result: unknown;
      try {
        result = await channel.apply(item.target);
      } catch (error) {
        if (gen === generation) failure(error, key);
        return;
      }
      if (gen !== generation) return;
      // L'avis du ⌘Z dit, une fois la réponse reçue, ce qu'il advient de l'ordre (OPO mis à jour, changements).
      const completed = stepNotice ? opts.complete(stepNotice, result) : null;
      if (completed) opts.notify(completed);
      appliedInGroup = true;
      if (!pending.has(key)) channel.preview?.(null);
    }
    if (gen === generation && !pending.size && timer === null) {
      groupStart = null;
      appliedInGroup = false;
    }
  };

  const flush = (): Promise<void> => {
    clearTimer();
    if (inFlight) {
      again = true;
      return inFlight;
    }
    if (!pending.size) {
      emit();
      return Promise.resolve();
    }
    const run = send(generation).finally(() => {
      inFlight = null;
      emit();
      if (again) {
        again = false;
        if (timer === null && pending.size) return flush();
      }
    });
    inFlight = run.then(() => undefined);
    emit();
    return inFlight;
  };

  const schedule = () => {
    clearTimer();
    timer = opts.setTimer(() => {
      timer = null;
      void flush();
    }, opts.flushMs);
  };

  /** Fait passer un canal à `target` (l'état du serveur étant `baseline` s'il n'attendait rien). */
  const move = (key: HistoryChannelKey, target: unknown, baseline: unknown, previousCursor: number) => {
    groupStart ??= previousCursor;
    const item = pending.get(key);
    if (item) item.target = target;
    else pending.set(key, { target, baseline });
    channels.get(key)?.preview?.(target);
    schedule();
  };

  const step = (direction: "undo" | "redo"): boolean => {
    if (!active) return false;
    const entry = direction === "undo" ? entries[cursor - 1] : entries[cursor];
    if (!entry || !usable(entry)) return false;
    const previous = cursor;
    cursor += direction === "undo" ? -1 : 1;
    for (const part of partsOf(entry)) {
      if (direction === "undo") move(part.channel, part.before, part.after, previous);
      else move(part.channel, part.after, part.before, previous);
    }
    stepNotice =
      direction === "undo"
        ? {
            text: t("Annulé : {action}", { action: entry.label }),
            tone: "info",
            action: { label: tc("refaire", "Rétablir"), run: () => void step("redo") },
          }
        : {
            text: t("Rétabli : {action}", { action: entry.label }),
            tone: "info",
            action: { label: tc("défaire", "Annuler"), run: () => void step("undo") },
          };
    opts.notify(stepNotice);
    emit();
    return true;
  };

  /** État du canal tel que le serveur l'a renvoyé (cache), normalisé. */
  const current = <S,>(key: HistoryChannelKey): S | null => {
    const state = channels.get(key)?.snapshot() ?? null;
    return state === null ? null : (normalizer(key)(state) as S);
  };

  const capture = <S,>(key: HistoryChannelKey): S | null => {
    // Une annulation attend son envoi : l'état que l'utilisateur voit (et retouche) est sa cible, pas le cache.
    const waiting = pending.get(key);
    return waiting ? (waiting.target as S) : current<S>(key);
  };

  const normalizedPart = (part: HistoryPart): HistoryPart => {
    const normalize = normalizer(part.channel);
    return { channel: part.channel, before: normalize(part.before), after: normalize(part.after) };
  };

  const push = (main: HistoryPart, parts: readonly HistoryPart[], label: string) => {
    const changed = [main, ...parts].map(normalizedPart).filter((part) => !equality(part.channel)(part.before, part.after));
    if (!changed.length) return;
    const [head, ...rest] = changed;
    const entry: HistoryEntry = { ...head!, label, ...(rest.length ? { parts: rest } : {}) };
    // Action faite pendant qu'un groupe d'annulations attend : l'état d'après fait foi pour ses canaux, leur cible en
    // attente n'est plus envoyée (elle écraserait l'action) ; en cas de refus d'un autre canal, l'historique
    // repartira de zéro.
    for (const part of changed) if (pending.delete(part.channel)) channels.get(part.channel)?.preview?.(null);
    if (groupStart !== null) appliedInGroup = true;
    if (!pending.size && inFlight === null) {
      clearTimer();
      groupStart = null;
      appliedInGroup = false;
    }
    stepNotice = null;
    entries = entries.slice(0, cursor);
    entries.push(entry);
    if (entries.length > opts.cap) entries = entries.slice(entries.length - opts.cap);
    cursor = entries.length;
    emit();
  };

  const record = <S,>(key: HistoryChannelKey, before: S, after: S, label: string) =>
    push({ channel: key, before, after }, [], label);

  const settle = async (): Promise<void> => {
    for (let guard = 0; guard < 100 && (timer !== null || inFlight !== null || pending.size); guard++) await flush();
  };

  return {
    store,
    registerChannel<S>(key: HistoryChannelKey, channel: HistoryChannel<S>) {
      const stored = channel as unknown as HistoryChannel<unknown>;
      channels.set(key, stored);
      emit();
      return () => {
        if (channels.get(key) === stored) channels.delete(key);
        emit();
      };
    },
    capture,
    record,
    commit<S>(key: HistoryChannelKey, before: S | null, label: string) {
      const after = current<S>(key); // réponse du serveur, déjà dans le cache
      if (before === null || after === null) return;
      record(key, before, after, label);
    },
    recordMany: (main: HistoryPart, parts: readonly HistoryPart[], label: string) => push(main, parts, label),
    undo: () => step("undo"),
    redo: () => step("redo"),
    reset,
    setContext(next: HistoryContext) {
      const changed =
        context !== null &&
        (context.dossier !== next.dossier || context.basis !== next.basis || context.opo !== next.opo);
      context = { ...next };
      if (changed) reset();
    },
    setActive(next: boolean) {
      if (active === next) return;
      active = next;
      emit();
    },
    isActive: () => active,
    configure(next: Partial<HistoryOptions>) {
      Object.assign(opts, next);
    },
    flush,
    settle,
  };
}

/** Historique de la carte, unique pour l'application (les avis passent par la barre d'avis de la carte). */
export const history: History = createHistory({ notify: (notice) => useUi.getState().notify(notice) });

// Un ⌘Z en attente de ses 300 ms part aussitôt si la page passe en arrière-plan ou se ferme (audit m11).
onExit(() => {
  if (history.store.getState().busy) void history.flush();
});

/** État de l'historique pour les boutons ↶ ↷ (sélecteur facultatif). */
export function useHistoryState<T = HistoryState>(selector?: (state: HistoryState) => T): T {
  return useStore(history.store, selector ?? ((s) => s as unknown as T));
}
